/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param variationAttributes - The products variation attributes.
 * @param values - The variations selected attribute values.
 * @returns A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ {
 *         "id": "color",
 *         "name": "Colour",
 *         "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ]
 *     } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */

import type { ShopperProducts, ShopperSearch } from '@/scapi';
import type { TFunction } from 'i18next';
import { findImageGroupBy } from '@/lib/image-groups-utils';
import { PDP_GALLERY_VIEW_TYPES, PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';
import type {
    SCAPIShopperBasketsV2Schemas,
    SCAPIShopperOrdersSchemas,
    SCAPIShopperProductsSchemas,
    SCAPIShopperSearchSchemas,
} from 'src/types';

/**
 * Type definition for swatch data used in color attribute selectors
 */
export type SwatchData = {
    imageUrl: string;
    alt: string;
    colorName: string;
    colorValue: string;
    isSelected?: boolean;
};

/**
 * Extract color values from variation attributes
 *
 * @param {ShopperProducts.schemas['VariationAttribute'][] | undefined} variationAttributes - The variation attributes to search
 * @returns {ShopperProducts.schemas['VariationAttributeValue'][]} Array of color variation values
 *
 * @example
 * const colorValues = getColorValues(product.variationAttributes);
 * // Returns all color variation values from the product
 */
export function getColorValues(
    variationAttributes: ShopperProducts.schemas['VariationAttribute'][] | undefined
): ShopperProducts.schemas['VariationAttributeValue'][] {
    if (variationAttributes && Array.isArray(variationAttributes)) {
        for (const attr of variationAttributes) {
            if (attr.id === 'color' && attr.values && Array.isArray(attr.values)) {
                return attr.values;
            }
        }
    }
    return [];
}

/**
 * Build swatch data from an image group for a specific color
 *
 * @param {ShopperProducts.schemas['ImageGroup']} imageGroup - The image group containing swatch images
 * @param {string} colorValue - The color value for this swatch
 * @param {string} colorName - The display name for this color
 * @returns {SwatchData[]} Array of swatch data objects
 *
 * @example
 * const swatches = buildImageSwatchData(imageGroup, 'red', 'Red');
 * // Returns swatch data for all images in the image group
 */
export function buildImageSwatchData(
    imageGroup: ShopperProducts.schemas['ImageGroup'],
    colorValue: string,
    colorName: string
): SwatchData[] {
    const swatchData: SwatchData[] = [];

    if (imageGroup.images && Array.isArray(imageGroup.images)) {
        imageGroup.images.forEach((image: ShopperProducts.schemas['Image']) => {
            const imageLink = image.disBaseLink || image.link;
            // Only add swatch if we have a valid color value and image link
            if (colorValue && imageLink) {
                swatchData.push({
                    imageUrl: imageLink,
                    alt: colorName || (image.alt as string) || `${colorValue} swatch`,
                    colorName,
                    colorValue,
                    isSelected: false, // Will be set later by the component
                });
            }
        });
    }

    return swatchData;
}

/**
 * Converts a record of variation attribute IDs and values into a record
 * of human-readable attribute names and value names.
 *
 * This is useful for displaying product variation selections (e.g. color, size)
 * in a more user-friendly format rather than showing internal IDs.
 *
 * @param {ShopperProducts.schemas['VariationAttribute'][]} [variationAttributes=[]]
 *   The list of variation attributes available for the product.
 *   Each attribute contains an `id`, a `name`, and a list of possible `values`.
 *
 * @param {Record<string, string>} [values={}]
 *   A record mapping variation attribute IDs to selected values.
 *   For example: `{ color: "red", size: "m" }`.
 *
 * @returns {Record<string, string>}
 *   A record mapping attribute display names to value display names.
 *   For example: `{ Color: "Red", Size: "Medium" }`.
 *
 * @example
 * const variationAttributes = [
 *   {
 *     id: "color",
 *     name: "Color",
 *     values: [
 *       { value: "red", name: "Red" },
 *       { value: "blue", name: "Blue" }
 *     ]
 *   },
 *   {
 *     id: "size",
 *     name: "Size",
 *     values: [
 *       { value: "s", name: "Small" },
 *       { value: "m", name: "Medium" }
 *     ]
 *   }
 * ];
 *
 * const values = { color: "red", size: "m" };
 *
 * getDisplayVariationValues(variationAttributes, values);
 * // => { Color: "Red", Size: "Medium" }
 */
export const getDisplayVariationValues = (
    variationAttributes: ShopperProducts.schemas['VariationAttribute'][] = [],
    values: Record<string, string> = {}
): Record<string, string> => {
    return Object.entries(values).reduce((acc: Record<string, string>, [id, value]) => {
        const attribute = variationAttributes.find(({ id: attributeId }) => attributeId === id);
        if (attribute && attribute.name) {
            const attributeValue = attribute.values?.find(({ value: attrValue }) => attrValue === value);
            if (attributeValue && attributeValue.name) {
                return {
                    ...acc,
                    [attribute.name]: attributeValue.name,
                };
            }
        }
        return acc;
    }, {});
};

/**
 * Creates a product URL with optional attribute parameter.
 * Centralizes the path creation logic for product links.
 *
 * @param {string | undefined} productId - The product ID to create the URL for.
 * @param {string | null} [selectedAttributeValue=null] - Optional attribute value to append as a query parameter.
 * @param {string} [attributeType='color'] - The attribute type for the query parameter.
 * @returns {string} The formatted product URL or '#' if productId is undefined.
 *
 * @example
 * createProductUrl({ c_url: '/p/test-product.html' }); // => '/p/test-product.html'
 * createProductUrl({ c_url: '/p/test-product.html' }, 'red'); // => '/p/test-product.html?color=red'
 * createProductUrl({ c_url: '/p/test-product.html' }, 'L', 'size'); // => '/p/test-product.html?size=L'
 * createProductUrl({ c_url: null }); // => '#'
 * createProductUrl(undefined); // => '#'
 */
export const createProductUrl = (
    productUrl: string,
    selectedAttributeValue: string | null = null,
    attributeType: string = 'color'
): string => {
    if (!selectedAttributeValue) {
        return productUrl || '#';
    }

    const searchParams = new URLSearchParams([[attributeType, selectedAttributeValue]]);
    return `${productUrl || '#'}?${searchParams.toString()}`;
};

/**
 * Get images filtered by color variation attribute
 * @param product - The product containing image groups
 * @param selectedColor - The selected color value to filter by
 * @param [viewType='SN'] - The image view type to filter by
 * @returns Array of images matching the color, or default images
 * @example
 * const images = getImagesForColor(product, 'red');
 * // Returns images for the red color variant, or default images if no match
 */
export function getImagesForColor(
    product: ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'],
    selectedColor: string | null,
    viewType: string = PRODUCT_IMAGE_VIEW_TYPES.SN
): ShopperProducts.schemas['Image'][] {
    // Return all images if no color is selected or no image groups exist
    if (!selectedColor || !product.imageGroups) {
        return product.imageGroups?.find((group) => group.viewType === viewType)?.images || [];
    }

    // Find image group that matches the selected color
    const imageGroup = findImageGroupBy(product.imageGroups, {
        viewType,
        selectedVariationAttributes: {
            color: selectedColor,
        },
    });

    // Return images from the matching group, or fallback to default images
    return imageGroup?.images || [];
}

/**
 * Decorated variation attribute with href and swatch image
 */
export type DecoratedVariationAttribute = ShopperProducts.schemas['VariationAttribute'] & {
    values: DecoratedVariationAttributeValue[];
};

/**
 * Decorated variation attribute value with href and swatch image
 */
export type DecoratedVariationAttributeValue = ShopperProducts.schemas['VariationAttributeValue'] & {
    href: string;
    swatch?: ShopperProducts.schemas['Image'];
};

/**
 * Provided a product this function will return the variation attributes decorated with
 * `href` and `swatch` image for the given attribute values. This allows easier access
 * when creating components that commonly use this information.
 *
 * @param {ShopperProducts.schemas['Product']} product - The product to decorate attributes for
 * @param {object} [opts={}] - Options for decoration
 * @param {string} [opts.swatchViewType='SN'] - The viewType for the swatch image
 *
 * @returns {DecoratedVariationAttribute[]} decoratedVariationAttributes
 */
export const getDecoratedVariationAttributes = (
    product: SCAPIShopperSearchSchemas['ProductSearchHit'],
    opts: { swatchViewType?: string } = {}
): DecoratedVariationAttribute[] => {
    const { swatchViewType = PRODUCT_IMAGE_VIEW_TYPES.SN } = opts;

    if (!product?.variationAttributes) {
        return [];
    }

    return product.variationAttributes.map((variationAttribute) => ({
        ...variationAttribute,
        values: (variationAttribute.values || []).map((value) => {
            // Create URL search params for this variation value
            const href = createProductUrl(product.c_url || '#', value.value, variationAttribute.id);

            // Find swatch image for this variation value
            const swatchImageGroup = findImageGroupBy(product.imageGroups || [], {
                viewType: swatchViewType,
                selectedVariationAttributes: {
                    [variationAttribute.id || '']: value.value,
                },
            });

            const swatch = swatchImageGroup?.images?.[0];

            return {
                ...value,
                href,
                swatch,
            };
        }),
    }));
};

/**
 * Determines if a product is a product set.
 * A product set is a collection of related products that can be purchased together.
 * @param product - The product to check
 * @returns true if the product is a product set, false otherwise
 */
export function isProductSet(product: ShopperProducts.schemas['Product']): boolean {
    return Boolean(product?.type?.set);
}

/**
 * Determines if a product is a physical product.
 * A physical product is a product that is not a digital product.
 * @param product - The product to check
 * @returns true if the product is a physical product, false otherwise
 */
export function isPhysicalProduct(product: ShopperProducts.schemas['Product']): boolean {
    return Boolean(product?.c_productType === 'physical');
}

type BasketProductItem = SCAPIShopperBasketsV2Schemas['ProductItem'];
type OrderProductItem = SCAPIShopperOrdersSchemas['ProductItem'];

function hasTypedProductItems(
    productItems: ReadonlyArray<BasketProductItem> | ReadonlyArray<OrderProductItem>
): boolean {
    return productItems.some((item) => item.c_productType != null && item.c_productType !== '');
}

/** True when the basket/order contains at least one shippable physical line item. */
export function basketHasPhysicalProducts(productItems?: BasketProductItem[] | null): boolean {
    return productItems?.some((item) => item.c_productType === 'physical') === true;
}

/** True when the basket has typed line items and none are physical merchandise. */
export function isVirtualOnlyBasket(productItems?: BasketProductItem[] | null): boolean {
    if (!productItems?.length || !hasTypedProductItems(productItems)) {
        return false;
    }

    return !basketHasPhysicalProducts(productItems);
}

/** True when the order has typed line items and none are physical merchandise. */
export function isVirtualOnlyOrder(productItems?: OrderProductItem[] | null): boolean {
    if (!productItems?.length || !hasTypedProductItems(productItems)) {
        return false;
    }

    return !productItems.some((item) => item.c_productType === 'physical');
}
/**
 * Determines if a product is a product bundle.
 * A product bundle is a group of products sold together as a single unit.
 * @param product - The product to check
 * @returns true if the product is a product bundle, false otherwise
 */
export function isProductBundle(product: ShopperProducts.schemas['Product']): boolean {
    return Boolean(product?.type?.bundle);
}

/**
 * Determines if a product is a standard product.
 * A standard product is a product that does not have variants.
 * @param product - The product to check
 * @returns true if the product is a standard product, false otherwise
 */
export function isStandardProduct(product: Partial<ShopperProducts.schemas['Product']>): boolean {
    return Boolean(product?.type?.item);
}

/**
 * Product types backed by the `c_productType` custom attribute that require the
 * dedicated event/course PDP layout (hero + booking box) instead of the standard
 * commerce product view.
 */
export type EventOrCourseProductType = 'event' | 'course';
export type ProductGiftEligibleType = EventOrCourseProductType | 'giftCard';

/**
 * Determines if a product is an event or a course (Eventbrite-sourced catalog).
 * These products render a dedicated PDP layout rather than the standard product view.
 *
 * @param product - The product to check
 * @returns true if `c_productType` is 'event' or 'course', false otherwise
 */
export function isEventOrCourseProduct(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>
): boolean {
    return product.c_productType === 'event' || product.c_productType === 'course';
}

export type EventbriteExternalCta = {
    reason: 'free' | 'commerceDisabled';
    url?: string;
};

type EventbriteExternalCtaOptions = {
    commerceEnabled?: boolean;
};

function normalizeEventbriteUrl(value: string | undefined): string | undefined {
    const url = value?.trim();
    if (!url) return undefined;

    try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? url : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Returns the external Eventbrite action for products that must bypass checkout.
 *
 * A missing URL is preserved in the decision so callers can suppress checkout
 * instead of accidentally allowing a free event into the cart.
 */
export function getEventbriteExternalCta(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType' | 'c_eventUrl' | 'price'>,
    { commerceEnabled = true }: EventbriteExternalCtaOptions = {}
): EventbriteExternalCta | null {
    if (!isEventOrCourseProduct(product)) return null;

    const reason = product.price === 0 ? 'free' : !commerceEnabled ? 'commerceDisabled' : null;
    if (!reason) return null;

    return {
        reason,
        url: normalizeEventbriteUrl(product.c_eventUrl),
    };
}

/**
 * Determines if a product is a course (Eventbrite-sourced catalog).
 *
 * @param product - The product to check
 * @returns true if `c_productType` is 'course', false otherwise
 */
export function isCourseProduct(product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>): boolean {
    return product.c_productType === 'course';
}

export function isVariantProduct(product: ShopperProducts.schemas['Product']): boolean {
    return Boolean(product?.type?.variant);
}

/**
 * Determines if a product supports the PDP gift-recipient flow.
 */
export function supportsProductGiftOption(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>
): product is Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'> & {
    c_productType: ProductGiftEligibleType;
} {
    return product.c_productType === 'giftCard' || isEventOrCourseProduct(product);
}

/**
 * Determines if the gift-recipient phone field is shown for a product type.
 */
export function includesGiftRecipientPhone(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>
): boolean {
    return isEventOrCourseProduct(product);
}

/**
 * Determines if the gift-recipient phone field is required for a product type.
 */
export function requiresGiftRecipientPhone(
    _product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>
): boolean {
    return false;
}

/**
 * Returns the product-type-specific copy used by the shared PDP gift flow.
 */
export function getProductGiftContent(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>,
    t: TFunction<'product'>
) {
    if (product.c_productType === 'course') {
        return {
            checkboxLabel: t('gift.checkboxCourse'),
            drawerTitle: t('gift.drawerTitleCourse'),
        };
    }

    if (product.c_productType === 'event') {
        return {
            checkboxLabel: t('gift.checkboxEvent'),
            drawerTitle: t('gift.drawerTitleEvent'),
        };
    }

    return {
        checkboxLabel: t('gift.checkboxGiftCard'),
        drawerTitle: t('gift.drawerTitleGiftCard'),
    };
}

/**
 * Determines if a product item is a bonus product.
 * A bonus product is a product that was added to the cart as part of a promotion.
 * @param productItem - The product item to check
 * @returns true if the product item is a bonus product, false otherwise
 */
export function isBonusProduct(productItem: SCAPIShopperBasketsV2Schemas['ProductItem']): boolean {
    return Boolean(productItem?.bonusProductLineItem);
}

/**
 * Determines the type of bonus product.
 * Returns 'choice' for choice-based bonus products (customers can select from options),
 * 'auto' for auto bonus products (automatically added), or null for non-bonus products.
 * @param productItem - The product item to check
 * @param bonusDiscountLineItems - Array of bonus discount line items from the basket
 * @returns 'choice' | 'auto' | null
 */
export function getBonusProductType(
    productItem: SCAPIShopperBasketsV2Schemas['ProductItem'],
    bonusDiscountLineItems?: SCAPIShopperBasketsV2Schemas['BonusDiscountLineItem'][]
): 'choice' | 'auto' | null {
    if (!isBonusProduct(productItem)) {
        return null;
    }

    const bonusDiscountLineItem = bonusDiscountLineItems?.find(
        (item) => item.id === productItem.bonusDiscountLineItemId
    );

    // If the bonusDiscountLineItem has bonusProducts array with items, it's choice-based
    if (bonusDiscountLineItem?.bonusProducts && bonusDiscountLineItem.bonusProducts.length > 0) {
        return 'choice';
    }

    return 'auto';
}

/**
 * Detects if a bonus discount line item is rule-based.
 * Rule-based promotions have empty bonusProducts arrays - products are
 * determined by dynamic rules and must be fetched via SCAPI product search.
 *
 * @param bonusDiscountLineItem - A single bonus discount line item from basket
 * @returns True if rule-based (empty bonusProducts array), false if list-based
 *
 * @example
 * // Rule-based promotion (dynamic rules)
 * const ruleBasedItem = {
 *   promotionId: 'promo-123',
 *   bonusProducts: []  // Empty array indicates rule-based
 * }
 * isRuleBasedPromotion(ruleBasedItem) // true
 */
export const isRuleBasedPromotion = (
    bonusDiscountLineItem?: SCAPIShopperBasketsV2Schemas['BonusDiscountLineItem'] | null
): boolean => {
    if (!bonusDiscountLineItem) {
        return false;
    }

    // Rule-based indicator: has a valid promotionId AND bonusProducts array is empty or doesn't exist
    const hasPromotionId = Boolean(bonusDiscountLineItem.promotionId);
    const bonusProducts = bonusDiscountLineItem.bonusProducts || [];
    const hasEmptyBonusProducts = bonusProducts.length === 0;

    return hasPromotionId && hasEmptyBonusProducts;
};

/**
 * Determines if a product requires variant selection before adding to cart.
 * Returns true for master products with variants or products with selectable attributes.
 *
 * @param product - The product to check
 * @returns true if variant selection is required, false for standard products
 */
export function requiresVariantSelection(product: ShopperProducts.schemas['Product']): boolean {
    // If this is already a variant (not a master), it can be added directly
    if (product.type?.variant) {
        return false;
    }

    // Check if it's a master product with variants
    if (product.variants && product.variants.length > 0) {
        return true;
    }

    // Check if it has variation attributes that need selection
    if (product.variationAttributes && product.variationAttributes.length > 0) {
        const hasSelectableAttributes = product.variationAttributes.some(
            (attr) => attr.values && attr.values.length > 1
        );
        return hasSelectableAttributes;
    }

    return false;
}

/**
 * Gets the primary image URL for a product from its image groups.
 * Uses the ordered physical-PDP view types.
 * For variants, uses variation values to find the correct color-specific image.
 *
 * @param product - The product with image groups
 * @param viewType - Optional explicit image view type
 * @param variationValues - Optional variation values for variant-specific images (e.g., {color: 'red'})
 * @returns Image URL or undefined if no images available
 */
export function getPrimaryProductImageUrl(
    product: ShopperProducts.schemas['Product'],
    viewType?: string,
    variationValues?: Record<string, string>
): string | undefined {
    const viewTypes = viewType ? [viewType] : PDP_GALLERY_VIEW_TYPES;

    for (const preferredViewType of viewTypes) {
        if (variationValues && Object.keys(variationValues).length > 0) {
            const imageGroup = findImageGroupBy(product.imageGroups || [], {
                viewType: preferredViewType,
                selectedVariationAttributes: variationValues,
            });
            if (imageGroup?.images?.[0]) {
                return imageGroup.images[0].disBaseLink || imageGroup.images[0].link;
            }
        }

        const imageGroup = product.imageGroups?.find((group) => group.viewType === preferredViewType);

        if (imageGroup?.images?.[0]) {
            return imageGroup.images[0].disBaseLink || imageGroup.images[0].link;
        }
    }

    return undefined;
}

/**
 * Converts a product map keyed by itemId to a product map keyed by productId.
 * Useful for converting checkout loader product maps to the format expected by hooks
 * like useBasketWithProducts.
 *
 * @param productsByItemId - Map of itemId to Product data (keyed by basket item ID)
 * @returns Map of productId to Product data (keyed by product catalog ID)
 *
 * @example
 * const productsByProductId = convertProductsByItemIdToProductId(productsByItemId);
 * // Returns products keyed by productId instead of itemId
 */
export function convertProductsByItemIdToProductId(
    productsByItemId: Record<string, ShopperProducts.schemas['Product']> | undefined
): Record<string, ShopperProducts.schemas['Product']> {
    if (!productsByItemId) {
        return {};
    }

    const productsByProductId: Record<string, ShopperProducts.schemas['Product']> = {};

    // Iterate over products and use product.id as the key
    // Deduplicate by only keeping the first occurrence of each productId
    Object.values(productsByItemId).forEach((product) => {
        if (product?.id && !productsByProductId[product.id]) {
            productsByProductId[product.id] = product;
        }
    });

    return productsByProductId;
}

/**
 * Enriched product item type that combines basket item data with product catalog data.
 * This type merges the ProductItem schema from the basket API with optional Product
 * schema fields from the product catalog, allowing access to additional product
 * information like imageGroups for display purposes.
 */
export type EnrichedProductItem = SCAPIShopperBasketsV2Schemas['ProductItem'] &
    Partial<SCAPIShopperProductsSchemas['Product']>;

/**
 * Enriches basket product items with full product data from productMap.
 * Merges product catalog data (images, variations, attributes) with basket item data,
 * finding the correct image group for each item's variation.
 *
 * @param productMap - Map of productId to Product data (keyed by product catalog ID)
 * @param productItems - Array of basket product items to enrich
 * @returns Array of enriched product items combining basket and product data
 *
 * @example
 * const enrichedItems = getEnrichedProducts(productMap, basket.productItems);
 * // Returns items with merged product data including correct variation images
 */
export function getEnrichedProducts(
    productMap: Record<string, ShopperProducts.schemas['Product']> | undefined,
    productItems?: SCAPIShopperBasketsV2Schemas['ProductItem'][]
): EnrichedProductItem[] {
    if (!productItems || !productMap) {
        return productItems || [];
    }

    return productItems.map((item) => {
        const productId = item.productId;
        if (!productId || !productMap[productId]) {
            return item;
        }

        const fullProduct = productMap[productId];

        // Find the correct image for this variation
        const imageGroup = findImageGroupBy(fullProduct.imageGroups, {
            viewType: PRODUCT_IMAGE_VIEW_TYPES.SN,
            selectedVariationAttributes: (item.variationValues as Record<string, string> | undefined) || {},
        });

        return {
            ...item,
            ...fullProduct,
            // Preserve basket-specific data (only override if item has the value)
            itemId: item.itemId,
            quantity: item.quantity,
            price: item.price,
            priceAfterItemDiscount: item.priceAfterItemDiscount,
            // Use product name for productName if available (for display consistency)
            productName: fullProduct.name || item.productName,
            // Keep fullProduct.variationValues unless item has its own
            variationValues: item.variationValues || fullProduct.variationValues,
            // Keep fullProduct.variationAttributes for proper display names
            variationAttributes: fullProduct.variationAttributes,
            // Use the correct image for the variation
            imageGroups: imageGroup ? [imageGroup] : fullProduct.imageGroups,
        };
    });
}
